import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useServices } from "@/app/use-services";
import { errorMessage } from "@/lib/api/errors";
import { BackLink } from "@/ui/BackLink";
import { Button, LinkButton } from "@/ui/Button";
import { Chip, ErrorNote, Input, Readout } from "@/ui/Field";
import { Page } from "@/ui/Page";
import { Row } from "@/ui/Row";
import { Body, Display, Label, Tiny } from "@/ui/Text";
import { LANGUAGE_LABEL, LANGUAGE_QUERY_KEY } from "./keys";

/** English or Hindi — the voice agent reads the same stored preference. */
export function LanguagePage() {
  const { api, analytics } = useServices();
  const client = useQueryClient();
  const language = useQuery({ queryKey: LANGUAGE_QUERY_KEY, queryFn: () => api.getLanguagePreference() });
  const set = useMutation({
    mutationFn: (code: string) => api.setLanguagePreference(code),
    onSuccess: (code) => {
      analytics.track({ name: "language changed", language: code });
      client.setQueryData(LANGUAGE_QUERY_KEY, code);
    },
  });
  const current = language.data ?? "en";
  return (
    <Page>
      <BackLink to="/you" label="You" />
      <Label className="mt-3">Language</Label>
      <Display className="mt-1.5">The language she answers in.</Display>
      <div role="radiogroup" aria-label="Language" className="mt-5 flex gap-2">
        {Object.entries(LANGUAGE_LABEL).map(([code, label]) => (
          <Chip key={code} label={label} selected={current === code} onSelect={() => set.mutate(code)} />
        ))}
      </div>
      <Tiny className="mt-4">Spoken and typed alike. You can switch mid-conversation just by switching.</Tiny>
      <ErrorNote message={set.isError ? errorMessage(set.error) : null} />
    </Page>
  );
}

/**
 * Both analytics tools, stated plainly, with one switch for both right here.
 * Google counts visits, sign-ups and purchases; PostHog records how the app
 * is used, with birth details and anything said to Antar hidden.
 */
function AnalyticsRow() {
  const { analytics } = useServices();
  const [consent, setConsent] = useState(() => analytics.consent());
  if (!analytics.enabled) {
    return <Row label="Analytics" serif={false} value="Off" note="No analytics runs in this version of the site." />;
  }
  const on = consent !== "denied";
  function toggle() {
    analytics.setConsent(!on);
    setConsent(on ? "denied" : "granted");
  }
  return (
    <Row
      label="Analytics"
      serif={false}
      value={on ? "On" : "Off"}
      note="Google Analytics counts visits, sign-ups and purchases, so we can tell which ads work. PostHog records how the app is used, including replays of taps, scrolls and screens. Your birth details, your email and anything said to or by Antar are hidden in those replays."
      trailing={<LinkButton label={on ? "Turn off" : "Turn on"} onClick={toggle} />}
    />
  );
}

/** What leaves the browser, and what never does. */
export function PrivacyPage() {
  return (
    <Page>
      <BackLink to="/you" label="You" />
      <Label className="mt-3">Privacy</Label>
      <Display className="mt-1.5">What leaves this browser, and what never does.</Display>
      <div className="mt-4">
        <AnalyticsRow />
        <Row label="What reaches our servers" serif={false} value="Your birth details, to compute your chart, and what you ask her, to answer it." note="Both are processed as described when you agreed to how Antar works, and deleted with your account." />
        <Row label="Never collected" serif={false} value="Your location, your contacts, and anything you did not type or say to her." note="The web app never asks for your address book. Friends are found by handle here." />
      </div>
    </Page>
  );
}

const PHRASE = "DELETE";

/** Deletion, armed by typing the word. A delete that did not happen is never reported as one that did. */
export function DeleteAccountPage() {
  const { api, session, draft, analytics } = useServices();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [typed, setTyped] = useState("");
  const armed = typed.trim().toUpperCase() === PHRASE;
  const remove = useMutation({
    mutationFn: () => api.deleteAccount(),
    onSuccess: async () => {
      analytics.track({ name: "account deleted" });
      draft.clear();
      client.clear();
      await session.signOut().catch(() => undefined);
      navigate("/", { replace: true });
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (armed && !remove.isPending) remove.mutate();
  }

  return (
    <form onSubmit={submit} className="contents">
      <Page
        foot={
          <>
            {!armed && <Tiny className="text-center">Type DELETE above to confirm.</Tiny>}
            <Button type="submit" label="Delete my account" outlined disabled={!armed} busy={remove.isPending} />
            <div className="text-center"><LinkButton label="Keep my account" onClick={() => navigate("/you")} disabled={remove.isPending} /></div>
          </>
        }
      >
        <BackLink to="/you" label="You" />
        <Label className="mt-3">This removes</Label>
        <Display size="md" className="mt-2.5">Your chart, your conversations, everything she remembers about you, your friends, and your public page.</Display>
        <Body className="mt-4">All of it goes at once, from our records as well as from this browser. Nothing is kept to bring back later, and this cannot be undone.</Body>
        <Tiny className="mt-2.5">If you subscribed on a phone, cancel the subscription in that app store as well — billing is held there, not here.</Tiny>
        <div className="mt-7">
          <Readout label="Type DELETE to confirm">
            <Input display={false} className="font-mono" autoCapitalize="characters" autoCorrect="off" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={PHRASE} disabled={remove.isPending} />
          </Readout>
        </div>
        {remove.isError && <Body className="mt-4">{errorMessage(remove.error)} Your account is still here — nothing was deleted.</Body>}
      </Page>
    </form>
  );
}
